import React, { useState } from 'react';
import {
  Box,
  Button,
  CardHeader,
  Grid,
  MenuItem,
  Modal,
  Typography,
  Tooltip,
  Select,
  Stack,
} from '@mui/material';
import PropTypes from 'prop-types';
import { useTheme } from '@mui/material/styles';
import makeStyles from "@material-ui/styles/makeStyles";
import CheckIcon from '@mui/icons-material/Check';
import CloseIcon from '@mui/icons-material/Close';
import ErrorIcon from '@mui/icons-material/Error';
import UploadFileIcon from '@mui/icons-material/UploadFile';
import { object, string } from 'zod';
import { zodResolver } from '@hookform/resolvers/zod';
import { useForm, FormProvider } from 'react-hook-form';
import { FileUploader } from "react-drag-drop-files";
import { useDispatch } from "react-redux";

import FormInput from '../form/FormInput';
import { createTicket } from "../../redux/actions/book";
import config from "../../config/config";

const FILE_TYPES = ["JPEG", "JPG", "PNG", "GIF"];
const PRODUCT_TYPES = [
  "My Bookings",
  "Booking Form",
  "Date Onboarding",
  "Loyalty Perks",
  "Subscriptions & Renewals",
  "Account Troubleshooting",
  "Other"
];
const PRIORITY_TYPES = ["High", "Medium", "Low"];


export default function CreateTicket({
  visible,
  onClose,
  onCreatedTicketSuccessed,
}) {
  const theme = useTheme();
  const dispatch = useDispatch();

  const useStyles = makeStyles({
    menuItem: {
      color: theme.palette.mode === 'light' ? '#5e6278' : '#92929f',
      fontSize: '13px',
      "&:hover": {
        color: '#009ef7',
        backgroundColor: theme.palette.mode === 'light' ? '#f1faff' : '#212e48',
      },
    },
    menuItemSelected: {
      color: '#fff',
      backgroundColor: '#009ef7 !important',
      alignItems: 'center',
      justifyContent: 'space-between',
      "&:hover": {
        color: '#fff',
        backgroundColor: '#009ef7 !important',
      },
    },
  });
  const styles = useStyles();

  const defaultValues = {
    subject: '',
    product: '',
    priority: '',
    description: ''
  };

  const createTicketSchema = object({
    subject: string().min(1, 'Ticket subject is required'),
    product: string().min(1, 'Ticket product is required'),
    priority: string().min(1, 'Ticket priority is required'),
    description: string().min(1, 'Ticket description is required'),
  });
  const methods = useForm({
    resolver: zodResolver(createTicketSchema),
    defaultValues
  });

  const [fileUploadError, setFileUploadError] = useState('');
  const [product, setProduct] = useState('');
  const [priority, setPriority] = useState('');
  const [attachmentFile, setAttachmentFile] = useState(null);
  const [attachmentFileSelected, setAttachmentFileSelected] = useState(null);


  const onChangeProduct = (e) => {
    setProduct(e.target.value);
    methods.setValue("product", e.target.value);
  }
  const onChangePriority = (e) => {
    setPriority(e.target.value);
    methods.setValue("priority", e.target.value);
  }

  const handleChangeAttachments = (files) => {
    setFileUploadError('');
    setAttachmentFile(files[0]);
    const reader = new FileReader();
    reader.readAsDataURL(files[0]);
    reader.onloadend = (e) => {
      setAttachmentFileSelected(reader.result);
    };
  };

  // ? Form Handler
  const onSubmitHandler = async (data) => {
    let formData = new FormData();
    formData.append("email", config.SUPPORT_TEAM_EMAIL_ADDRESS);
    formData.append('subject', data.subject);
    formData.append('product', data.product);
    formData.append("priority", data.priority);
    formData.append("description", data.description);
    if (attachmentFile) {
      formData.append("attachments", attachmentFile);
    }
    const { success } = await dispatch(createTicket(formData));
    if (success) {
      setProduct('');
      setPriority('');
      setAttachmentFile(null);
      methods.reset();
      onCreatedTicketSuccessed();
    }
  };

  const handleAttachmentRemove = () => {
    setAttachmentFile(null);
    setAttachmentFileSelected(null);
  }

  return (
    <Modal
      open={visible}
      aria-labelledby="modal-modal-title"
      aria-describedby="modal-modal-description"
    >
      <Box
        sx={{
          position: 'absolute',
          top: '50%',
          left: '50%',
          transform: 'translate(-50%, -50%)',
          width: { xs: '95%', md: '650px'},
          bgcolor: 'background.paper',
          borderRadius: 2,
          boxShadow: 24,
          maxHeight: '95%',
          overflowY: 'auto',
          '::-webkit-scrollbar': {
            width: '12px',
          },
          '::-webkit-scrollbar-track': {
            backgroundColor: 'transparent',
          },
          '::-webkit-scrollbar-thumb': {
            borderRadius: '6px',
            border: '3px solid transparent',
            backgroundClip: 'content-box',
            backgroundColor: '#c1c1c1',
          }
        }}
      >
        <Grid sx={{ margin: 'auto', p: 4 }} className="text-center">
          <CloseIcon
            onClick={onClose}
            sx={{
              color: theme.palette.mode === 'light' ? '#181c32' : '#fff',
              position: 'absolute',
              right: '16px',
              top: '16px', 
              display: { md: 'none' }
            }}
          />
          <Typography id="modal-modal-title" sx={{ fontSize: '24px', fontWeight: 800, textAlign: 'center' }}>
            Create Ticket
          </Typography>
          <Typography id="modal-modal-description" sx={{ mt: 0.5, fontSize: '14px', textAlign: 'center', color: theme.palette.mode === 'light' ? '#7e8299' : '#6d6d80' }}>
            Please fill in the form below to submit a support ticket<br />Alternatively, email us at <strong style={{ color: '#009ef7' }}>{config.SUPPORT_TEAM_EMAIL_ADDRESS}</strong>
          </Typography>
          <FormProvider {...methods}>
            <Box
              display='flex'
              flexDirection='column'
              component='form'
              noValidate
              autoComplete='off'
              sx={{ mt: 2 }}
              onSubmit={methods.handleSubmit(onSubmitHandler)}
            >
              <Grid>
                <Stack direction="row" alignItems="center">
                  <Typography sx={{ fontSize: '14px', fontWeight: '500', color: theme.palette.mode === 'light' ? '#181c32' : '#fff' }}>Subject <span style={{ color: 'red' }}>*</span></Typography>
                </Stack>
                <FormInput
                  placeholder='Enter your ticket subject'
                  type='text'
                  name='subject'
                  fullWidth
                  sx={{ mt: 1 }}
                />
              </Grid>
              <Grid container spacing={3} sx={{ mt: 0.5 }}>
                <Grid item xs={6}>
                  <Typography sx={{ fontSize: '14px', fontWeight: '500', textAlign: 'left', color: theme.palette.mode === 'light' ? '#181c32' : '#fff' }}>Topic <span style={{ color: 'red' }}>*</span></Typography>
                  <Select
                    fullWidth
                    inputProps={{ sx: { color: '#5e6278', fontWeight: 600 } }}
                    displayEmpty={true}
                    value={product}
                    renderValue={value => value ? value : 'Select an area'}
                    sx={{ mt: 1, textAlign: 'left', padding: 0, '.MuiSelect-select': { padding: '12px 20px' }, '.MuiOutlinedInput-notchedOutline': { borderStyle: 'none' } }}
                    onChange={onChangeProduct}
                  >
                    {PRODUCT_TYPES.map((item, index) => (
                      <MenuItem
                        key={item}
                        value={item}
                        selected={item === product}
                        classes={{ root: styles.menuItem, selected: styles.menuItemSelected }}
                      >
                        {item}
                        {item === product && <CheckIcon style={{ width: '15px', height: '15px' }} />}
                      </MenuItem>
                    ))}
                  </Select>
                </Grid>
                <Grid item xs={6}>
                  <Typography sx={{ fontSize: '14px', fontWeight: '500', textAlign: 'left', color: theme.palette.mode === 'light' ? '#181c32' : '#fff' }}>Priority <span style={{ color: 'red' }}>*</span></Typography>
                  <Select
                    fullWidth
                    inputProps={{ sx: { color: '#5e6278', fontWeight: 600 } }}
                    displayEmpty={true}
                    value={priority}
                    renderValue={value => value ? value : 'Select Priority'}
                    sx={{ mt: 1, textAlign: 'left', padding: 0, '.MuiSelect-select': { padding: '12px 20px' }, '.MuiOutlinedInput-notchedOutline': { borderStyle: 'none' } }}
                    onChange={onChangePriority}
                  >
                    {PRIORITY_TYPES.map((item, index) => (
                      <MenuItem
                        key={item}
                        value={item}
                        selected={item === priority}
                        classes={{ root: styles.menuItem, selected: styles.menuItemSelected }}
                      >
                        {item}
                        {item === priority && <CheckIcon style={{ width: '15px', height: '15px' }} />}
                      </MenuItem>
                    ))}
                  </Select>
                </Grid>
              </Grid>
              <Grid sx={{ mt: 1 }}>
                <Typography sx={{ fontSize: '14px', fontWeight: 600, textAlign: 'left', mt: 2 }}>Description <span style={{ color: 'red' }}>*</span></Typography>
                <FormInput
                  placeholder='Please describe your issue or enquiry in detail'
                  type='text'
                  name='description'
                  fullWidth
                  multiline
                  rows={5}
                  sx={{ mt: 1 }}
                />
              </Grid>
              <Grid sx={{ mt: 1, textAlign: 'left' }}>
                <Typography sx={{ fontSize: '14px', fontWeight: 600, textAlign: 'left', mt: 2 }}>Additional Attachment</Typography>
                <FileUploader
                  multiple={true}
                  handleChange={handleChangeAttachments}
                  name="file"
                  types={FILE_TYPES}
                  children={
                    <CardHeader
                      avatar={<UploadFileIcon sx={{ fontSize: '2.5rem', color: '#009EF7' }} />}
                      title="Drop image here or click to upload."
                      titleTypographyProps={{ fontSize: '15px', fontWeight: 600, textAlign: 'left', color: fileUploadError ? 'tomato' : theme.palette.mode === 'light' ? '#3f4254' : '#fff' }}
                      subheader={fileUploadError ? fileUploadError : "File size limit 2MB"}
                      subheaderTypographyProps={{ fontWeight: 600, color: fileUploadError ? 'tomato' : theme.palette.mode === 'light' ? '#a1a5b7' : '#474761', fontSize: '12px', textAlign: 'left' }}
                      sx={{
                        border: '1px',
                        borderStyle: 'dashed',
                        borderColor: fileUploadError ? 'red' : '#009EF7',
                        borderRadius: 1,
                        mt: 1,
                        backgroundColor: theme.palette.mode === 'light' ? '#F1FAFF' : '#212E48',
                        '&:hover ': {
                          cursor: 'pointer'
                        }
                      }}
                    />
                  }
                  onTypeError={(err) => setFileUploadError(err)}
                  onSizeError={(err) => console.log(err)}
                />
                <Box display="inline" sx={{ boxShadow: 0, borderRadius: '6px', padding: '0px', mt: 3, position: 'relative', width: '95%' }}>
                  <Box
                    component="img"
                    src={attachmentFileSelected}
                    sx={{ width: '200px', objectFit: 'contain' }}
                  />
                  {attachmentFileSelected && (
                    <Tooltip title="Remove Logo" placement="top" enterTouchDelay={0}>
                      <span className="Image-remove" onClick={handleAttachmentRemove}>
                        <i className="fa fa-remove"></i>
                      </span>
                    </Tooltip>
                  )}
                </Box>
              </Grid>
              <Grid item container direction='row' justifyContent='center' alignItems='center' display="flex" sx={{ mt: 2 }}>
                <Button
                  type="submit"
                  variant="contained"
                  sx={{ padding: '11px 35px', fontSize: '14px', lineHeight: '21px', fontWeight: 600, textTransform: 'none', color: '#fff' }}
                >
                  Submit
                </Button>
                <Button
                  onClick={onClose}
                  variant="contained"
                  sx={{
                    backgroundColor: theme.palette.mode === 'light' ? '#f5f8fa' : '#2b2b40',
                    padding: '11px 30px',
                    fontSize: '14px',
                    fontWeight: 600,
                    textTransform: 'none',
                    color: '#6d6d80',
                    marginInlineStart: 1,
                    '&:hover ': {
                      backgroundColor: theme.palette.mode === 'light' ? '#eff2f5' : '#323248',
                    }
                  }}
                >
                  Cancel
                </Button>
              </Grid>
            </Box>
          </FormProvider>
        </Grid>
      </Box>
    </Modal>
  );
}

CreateTicket.propTypes = {
  visible: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired
};
